import { MetricCard } from './MetricCard'
import { useDashboardMetrics } from '../hooks/useDashboard'
import { Users, Building2, Handshake, TrendingUp, DollarSign, Target } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * DashboardMetrics Component
 *
 * Displays key CRM metrics in a grid layout
 * - Total contacts, companies, deals
 * - Active deals
 * - Conversion rate
 * - Total revenue
 */
export function DashboardMetrics() {
  const { data: metrics, isLoading, error } = useDashboardMetrics()

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
        <p className="text-sm text-destructive">
          Failed to load dashboard metrics. Please try again later.
        </p>
      </div>
    )
  }

  if (!metrics) {
    return null
  }

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {/* Contacts */}
      <MetricCard
        title="Total Contacts"
        value={metrics.contacts}
        description="People in your CRM"
        icon={Users}
      />

      {/* Companies */}
      <MetricCard
        title="Total Companies"
        value={metrics.companies}
        description="Organizations tracked"
        icon={Building2}
      />

      {/* Total Deals */}
      <MetricCard
        title="Total Deals"
        value={metrics.deals.total}
        description="All deals in pipeline"
        icon={Handshake}
      />

      {/* Active Deals */}
      <MetricCard
        title="Active Deals"
        value={metrics.deals.active}
        description="Currently in progress"
        icon={TrendingUp}
      />

      {/* Conversion Rate */}
      <MetricCard
        title="Conversion Rate"
        value={`${metrics.deals.conversionRate}%`}
        description="Deals won vs total"
        icon={Target}
      />

      {/* Total Revenue */}
      <MetricCard
        title="Total Revenue"
        value={formatCurrency(metrics.deals.revenue)}
        description="From closed won deals"
        icon={DollarSign}
      />
    </div>
  )
}
